import { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import {
	callAPI,
	type ActiveFinancingOverrides,
	type FinancingOverrideProduct,
	type FinancingOverrideProductType
} from '../../../hooks/api';
import { UserData } from '../../../hooks/auth';
import { Button } from '../../../shadcn/Button';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { Dialog } from '../../../components/Dialog';
import Input from '../../../components/Input';
import Notice from '../../../components/Notice';
import Select from '../../../components/Select';
import Spinner from '../../../components/Spinner';

type StoredOverrides = ActiveFinancingOverrides & { expires_at?: string };
type OverrideField = 'payment_options' | 'down_payment' | 'price_range' | 'min_amount' | 'fee';

// Mirrors krece-backend FINANCING_PRODUCT_KEYS / FINANCING_DEVICE_KEYS
const DEVICE_FIELDS: OverrideField[] = ['payment_options', 'down_payment', 'price_range'];
const PRODUCTS: { key: FinancingOverrideProductType; label: string; fields: OverrideField[] }[] = [
	{ key: 'phone', label: 'Teléfonos', fields: DEVICE_FIELDS },
	{ key: 'iphone', label: 'iPhone', fields: DEVICE_FIELDS },
	{ key: 'tablet', label: 'Tablets', fields: DEVICE_FIELDS },
	{ key: 'laptop', label: 'Laptops', fields: DEVICE_FIELDS },
	{ key: 'other', label: 'Otros productos', fields: DEVICE_FIELDS },
	{ key: 'services', label: 'Servicios', fields: DEVICE_FIELDS },
	{ key: 'insurance', label: 'Seguros', fields: ['payment_options'] },
	{ key: 'loan', label: 'Préstamos', fields: ['payment_options', 'min_amount'] },
	{ key: 'recarga', label: 'Recargas y pago de servicios', fields: ['fee'] }
];
const FIELD_LABELS: Record<OverrideField, string> = {
	payment_options: 'Cuotas',
	down_payment: 'Inicial',
	price_range: 'Rango de precio',
	min_amount: 'Monto mínimo',
	fee: 'Comisión'
};

type PlanDraft = { installments: string; interest: string; original?: { installments: number; factor: number } };
type ProductDraft = {
	key: FinancingOverrideProductType;
	payment_options: PlanDraft[] | null;
	down_payment: { min: string; max: string; granularity: string } | null;
	price_range: { min: string; max: string } | null;
	min_amount: string | null;
	fee: string | null;
};

const interest_text = (factor: number) => String(Number(((factor - 1) * 100).toFixed(4)));

const plan_draft = (option: { installments: number; factor: number }): PlanDraft => ({
	installments: String(option.installments),
	interest: interest_text(option.factor),
	original: option
});

const product_draft = (key: FinancingOverrideProductType, product: FinancingOverrideProduct = {}): ProductDraft => ({
	key,
	payment_options: product.payment_options ? product.payment_options.map(plan_draft) : null,
	down_payment: product.down_payment
		? {
				min: String(product.down_payment.min),
				max: String(product.down_payment.max),
				granularity: String(product.down_payment.granularity)
			}
		: null,
	price_range: product.price_range ? { min: String(product.price_range.min), max: String(product.price_range.max) } : null,
	min_amount: product.min_amount !== undefined ? String(product.min_amount) : null,
	fee: product.fee !== undefined ? String(product.fee) : null
});

// A new product starts with the field it can't do without
const empty_product = (key: FinancingOverrideProductType): ProductDraft => ({
	...product_draft(key),
	...(key === 'recarga' ? { fee: '' } : { payment_options: [{ installments: '', interest: '' }] })
});

// datetime-local wants local wall-clock time without a zone
const to_local_input = (iso: string) => {
	const date = new Date(iso);
	if (isNaN(date.getTime())) return '';
	return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const format_date = (iso: string) =>
	new Date(iso).toLocaleString('es-ES', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const parse_number = (value: string) => {
	const trimmed = value.trim().replace(',', '.');
	return trimmed && /^\d+(\.\d+)?(e[-+]?\d+)?$/i.test(trimmed) ? Number(trimmed) : NaN;
};

const parse_int = (value: string) => (/^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN);

/** Builds the PUT body from the form; the backend validates everything again. */
function build_overrides(
	products: ProductDraft[],
	expires_at: string,
	stored_expiry?: { local: string; iso: string } | null
): [StoredOverrides | null, string | null] {
	const result: StoredOverrides = {};
	for (const draft of products) {
		const label = PRODUCTS.find((product) => product.key === draft.key)?.label || draft.key;
		const product: FinancingOverrideProduct = {};
		if (draft.payment_options) {
			if (!draft.payment_options.length) return [null, `${label}: agrega al menos un plazo o quita las cuotas.`];
			const options = [];
			for (const plan of draft.payment_options) {
				// An untouched row keeps its exact stored numbers
				const untouched =
					plan.original &&
					plan.installments === String(plan.original.installments) &&
					plan.interest === interest_text(plan.original.factor);
				const installments = untouched && plan.original ? plan.original.installments : parse_int(plan.installments);
				const interest = parse_number(plan.interest);
				const factor = untouched && plan.original ? plan.original.factor : Math.round((1 + interest / 100) * 1e6) / 1e6;
				if (!Number.isFinite(installments) || installments < 1 || !Number.isFinite(factor)) {
					return [null, `${label}: revisa las cuotas y el interés de cada plazo.`];
				}
				options.push({ installments, factor });
			}
			if (new Set(options.map((option) => option.installments)).size !== options.length) {
				return [null, `${label}: hay plazos repetidos.`];
			}
			product.payment_options = options.sort((a, b) => a.installments - b.installments);
		}
		if (draft.down_payment) {
			const down_payment = {
				min: parse_int(draft.down_payment.min),
				max: parse_int(draft.down_payment.max),
				granularity: parse_int(draft.down_payment.granularity)
			};
			if (Object.values(down_payment).some((value) => !Number.isFinite(value))) {
				return [null, `${label}: la inicial usa porcentajes enteros (mínimo, máximo y salto).`];
			}
			product.down_payment = down_payment;
		}
		if (draft.price_range) {
			const price_range = { min: parse_number(draft.price_range.min), max: parse_number(draft.price_range.max) };
			if (!Number.isFinite(price_range.min) || !Number.isFinite(price_range.max)) {
				return [null, `${label}: revisa el rango de precio.`];
			}
			product.price_range = price_range;
		}
		if (draft.min_amount !== null) {
			const min_amount = parse_number(draft.min_amount);
			if (!Number.isFinite(min_amount)) return [null, `${label}: revisa el monto mínimo.`];
			product.min_amount = min_amount;
		}
		if (draft.fee !== null) {
			const fee = parse_number(draft.fee);
			if (!Number.isFinite(fee)) return [null, `${label}: revisa la comisión.`];
			product.fee = fee;
		}
		if (!Object.keys(product).length) return [null, `${label}: configura al menos una condición o quita el producto.`];
		result[draft.key] = product;
	}
	if (expires_at && Object.keys(result).length) {
		// The input only holds minutes: an untouched date keeps the exact stored instant
		const untouched = stored_expiry?.local === expires_at;
		const expiry = new Date(untouched && stored_expiry ? stored_expiry.iso : expires_at);
		if (isNaN(expiry.getTime())) return [null, 'La fecha de vencimiento no es válida.'];
		if (expiry.getTime() <= Date.now()) return [null, 'La fecha de vencimiento ya pasó: cámbiala o déjala vacía.'];
		result.expires_at = untouched && stored_expiry ? stored_expiry.iso : expiry.toISOString();
	}
	return [result, null];
}

/**
 * A customer's own financing conditions (Users.financing_overrides). Anyone who can open the
 * customer sees them; only admins change them, through the backend (validated and audited).
 */
export default function CustomerFinancingOverrides({
	user_email,
	country,
	userData,
	currency_code
}: {
	user_email: string;
	country: string | null;
	userData: UserData | null;
	currency_code: string;
}) {
	const can_edit = userData?.access_type === 'admin';
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [stored, setStored] = useState<StoredOverrides>({});
	const [active, setActive] = useState<ActiveFinancingOverrides>({});
	// null = the stored map no longer validates and may only be replaced knowingly
	const [expected, setExpected] = useState<StoredOverrides | null>({});
	const [editing, setEditing] = useState(false);
	const [confirmRemove, setConfirmRemove] = useState(false);
	const [drafts, setDrafts] = useState<ProductDraft[]>([]);
	const [expiresAt, setExpiresAt] = useState('');
	const [formError, setFormError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		setLoadError(null);
		const [data, error] = await callAPI(
			'https://api.krece.app/users/financing-overrides/',
			'GET',
			{ user_email },
			userData,
			country
		);
		if (error || !data) {
			setLoadError(error || 'No se pudieron consultar las condiciones personalizadas.');
			setStored({});
			setActive({});
			setExpected(null);
		} else {
			setStored(data.financing_overrides || {});
			setActive(data.active_financing_overrides || {});
			setExpected(data.financing_overrides || {});
		}
		setLoading(false);
	}, [user_email, userData, country]);

	useEffect(() => {
		load();
	}, [load]);

	const money = (amount: number) => `${currency_code === 'S/' ? '' : currency_code}${amount.toFixed(2)}`;
	const storedProducts = PRODUCTS.filter((product) => stored[product.key]);
	const isActive = Object.keys(active).length > 0;

	const openEditor = () => {
		setDrafts(storedProducts.map((product) => product_draft(product.key, stored[product.key])));
		setExpiresAt(stored.expires_at ? to_local_input(stored.expires_at) : '');
		setFormError(null);
		setEditing(true);
	};

	const save = async (financing_overrides: StoredOverrides) => {
		const [, error] = await callAPI(
			'https://api.krece.app/users/financing-overrides/',
			'PUT',
			{ user_email, financing_overrides, expected_financing_overrides: expected },
			userData,
			country
		);
		if (error) return error;
		await load();
		return true;
	};

	const submit = async () => {
		const [financing_overrides, error] = build_overrides(
			drafts,
			expiresAt,
			stored.expires_at ? { local: to_local_input(stored.expires_at), iso: stored.expires_at } : null
		);
		if (!financing_overrides) {
			setFormError(error);
			return;
		}
		setSaving(true);
		setFormError(null);
		const result = await save(financing_overrides);
		setSaving(false);
		if (result === true) setEditing(false);
		else setFormError(result);
	};

	const updateDraft = (key: FinancingOverrideProductType, change: Partial<ProductDraft>) =>
		setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...change } : draft)));

	const summary = (product: FinancingOverrideProduct) =>
		[
			product.payment_options
				?.map(
					(option) =>
						`${option.installments} cuota${option.installments === 1 ? '' : 's'} (${interest_text(option.factor)}% interés)`
				)
				.join(', '),
			product.down_payment &&
				`Inicial ${product.down_payment.min}%–${product.down_payment.max}% (saltos de ${product.down_payment.granularity}%)`,
			product.price_range && `Precio ${money(product.price_range.min)}–${money(product.price_range.max)}`,
			product.min_amount !== undefined && `Monto mínimo ${money(product.min_amount)}`,
			product.fee !== undefined && `Comisión ${money(product.fee)}`
		]
			.filter(Boolean)
			.join(' · ');

	return (
		<div className="border-t border-neutral-800 pt-4">
			<div className="mb-3 flex items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<h3 className="text-lg font-semibold text-neutral-100">Condiciones personalizadas</h3>
					{can_edit && !loading && !loadError && (
						<button
							onClick={openEditor}
							className="rounded-full p-1 hover:bg-neutral-700/50"
							aria-label="Editar condiciones personalizadas"
						>
							<Pencil className="h-3.5 w-3.5 text-neutral-400 hover:text-neutral-300" />
						</button>
					)}
				</div>
				{can_edit && !loading && !loadError && storedProducts.length > 0 && (
					<button onClick={() => setConfirmRemove(true)} className="text-xs font-medium text-red-400 hover:text-red-300">
						Quitar todas
					</button>
				)}
			</div>

			{loading ? (
				<Spinner size={1.25} />
			) : loadError ? (
				<div className="space-y-2">
					<Notice message={loadError} variant="error" />
					{can_edit && (
						<Button variant="secondary" className="!w-auto !py-2" onClick={openEditor}>
							Reemplazar configuración
						</Button>
					)}
				</div>
			) : !storedProducts.length ? (
				<p className="text-sm text-neutral-400">Este cliente usa las condiciones de su nivel.</p>
			) : (
				<div className="space-y-2">
					<div className="flex items-center gap-1.5">
						<div className={`h-2 w-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-neutral-500'}`} />
						<span className={`text-xs ${isActive ? 'text-green-400' : 'text-neutral-400'}`}>
							{isActive
								? stored.expires_at
									? `Activas hasta el ${format_date(stored.expires_at)}`
									: 'Activas sin vencimiento'
								: stored.expires_at
									? `Vencidas el ${format_date(stored.expires_at)}. Ya no se aplican.`
									: 'No se aplican.'}
						</span>
					</div>
					{storedProducts.map((product) => (
						<div
							key={product.key}
							className="flex min-h-[40px] flex-col gap-1 rounded-md bg-neutral-800/30 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
						>
							<span className="text-sm text-neutral-400">{product.label}</span>
							<span className="text-sm text-neutral-100 sm:text-right">{summary(stored[product.key] || {})}</span>
						</div>
					))}
				</div>
			)}

			{editing && (
				<Dialog
					title="Condiciones personalizadas"
					onClose={() => !saving && setEditing(false)}
					closable={!saving}
					cerrarButton={false}
					className="!w-[min(640px,92vw)]"
				>
					<form
						className="flex flex-col gap-4"
						onSubmit={(event) => {
							event.preventDefault();
							submit();
						}}
					>
						<p className="text-sm text-neutral-400">
							Cada condición reemplaza la del nivel del cliente para ese producto; lo que no configures sigue igual. Solo aplica a
							operaciones nuevas.
						</p>

						{drafts.map((draft) => {
							const product = PRODUCTS.find((item) => item.key === draft.key);
							if (!product) return null;
							const missing = product.fields.filter((field) => draft[field] === null);
							return (
								<div key={draft.key} className="space-y-3 rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-3">
									<div className="flex items-center justify-between">
										<h4 className="text-sm font-medium text-neutral-100">{product.label}</h4>
										<button
											type="button"
											onClick={() => setDrafts((current) => current.filter((item) => item.key !== draft.key))}
											className="rounded-full p-1 hover:bg-neutral-700/50"
											aria-label={`Quitar ${product.label}`}
										>
											<X className="h-3.5 w-3.5 text-neutral-400" />
										</button>
									</div>

									{draft.payment_options && (
										<div className="space-y-2">
											<FieldHeader label="Cuotas e interés" onRemove={() => updateDraft(draft.key, { payment_options: null })} />
											{draft.payment_options.map((plan, index) => (
												<div key={index} className="flex items-center gap-1">
													<Input
														placeholder="Cuotas"
														value={plan.installments}
														onChange={(value) =>
															updateDraft(draft.key, {
																payment_options: draft.payment_options!.map((row, i) =>
																	i === index ? { ...row, installments: value } : row
																)
															})
														}
														allowedChars="numbers_only"
														clearable={false}
														containerClassName="flex-1"
													/>
													<Input
														placeholder="% interés"
														value={plan.interest}
														onChange={(value) =>
															updateDraft(draft.key, {
																payment_options: draft.payment_options!.map((row, i) =>
																	i === index ? { ...row, interest: value } : row
																)
															})
														}
														allowedChars="numeric"
														clearable={false}
														containerClassName="flex-1"
													/>
													<button
														type="button"
														onClick={() =>
															updateDraft(draft.key, { payment_options: draft.payment_options!.filter((_, i) => i !== index) })
														}
														className="shrink-0 rounded-lg bg-red-600 p-1.5 text-white hover:bg-red-700"
														aria-label="Eliminar plazo"
													>
														<Trash2 className="h-3 w-3" />
													</button>
												</div>
											))}
											<button
												type="button"
												onClick={() =>
													updateDraft(draft.key, {
														payment_options: [...draft.payment_options!, { installments: '', interest: '' }]
													})
												}
												className="inline-flex items-center gap-1 text-xs font-medium text-primary-400 hover:text-primary-300"
											>
												<Plus className="h-3 w-3" />
												Agregar plazo
											</button>
										</div>
									)}

									{draft.down_payment && (
										<div className="space-y-2">
											<FieldHeader label="Inicial (%)" onRemove={() => updateDraft(draft.key, { down_payment: null })} />
											<div className="flex gap-1">
												{(['min', 'max', 'granularity'] as const).map((part) => (
													<Input
														key={part}
														placeholder={part === 'min' ? 'Mínimo %' : part === 'max' ? 'Máximo %' : 'Saltos de %'}
														value={draft.down_payment![part]}
														onChange={(value) =>
															updateDraft(draft.key, { down_payment: { ...draft.down_payment!, [part]: value } })
														}
														allowedChars="numbers_only"
														clearable={false}
														containerClassName="flex-1"
													/>
												))}
											</div>
										</div>
									)}

									{draft.price_range && (
										<div className="space-y-2">
											<FieldHeader label="Rango de precio" onRemove={() => updateDraft(draft.key, { price_range: null })} />
											<div className="flex gap-1">
												{(['min', 'max'] as const).map((part) => (
													<Input
														key={part}
														placeholder={part === 'min' ? 'Precio mínimo' : 'Precio máximo'}
														value={draft.price_range![part]}
														onChange={(value) =>
															updateDraft(draft.key, { price_range: { ...draft.price_range!, [part]: value } })
														}
														allowedChars="numeric"
														clearable={false}
														containerClassName="flex-1"
													/>
												))}
											</div>
										</div>
									)}

									{draft.min_amount !== null && (
										<div className="space-y-2">
											<FieldHeader
												label="Monto mínimo del préstamo"
												onRemove={() => updateDraft(draft.key, { min_amount: null })}
											/>
											<Input
												placeholder="Monto mínimo"
												value={draft.min_amount}
												onChange={(value) => updateDraft(draft.key, { min_amount: value })}
												allowedChars="numeric"
												clearable={false}
											/>
										</div>
									)}

									{draft.fee !== null && (
										<div className="space-y-2">
											<span className="text-xs font-medium text-neutral-400">
												Comisión por recarga (0 = sin comisión{country === 'venezuela' ? '; antes de IVA' : ''})
											</span>
											<Input
												placeholder="Comisión"
												value={draft.fee}
												onChange={(value) => updateDraft(draft.key, { fee: value })}
												allowedChars="numeric"
												clearable={false}
											/>
										</div>
									)}

									{missing.length > 0 && (
										<div className="flex flex-wrap gap-3">
											{missing.map((field) => (
												<button
													key={field}
													type="button"
													onClick={() =>
														updateDraft(draft.key, {
															[field]:
																field === 'payment_options'
																	? [{ installments: '', interest: '' }]
																	: field === 'down_payment'
																		? { min: '', max: '', granularity: '' }
																		: field === 'price_range'
																			? { min: '', max: '' }
																			: ''
														})
													}
													className="inline-flex items-center gap-1 text-xs font-medium text-primary-400 hover:text-primary-300"
												>
													<Plus className="h-3 w-3" />
													{FIELD_LABELS[field]}
												</button>
											))}
										</div>
									)}
								</div>
							);
						})}

						{drafts.length < PRODUCTS.length && (
							<Select
								// Remounted per change so a removed product can be picked again
								key={drafts.map((draft) => draft.key).join(',')}
								placeholder="Agregar producto"
								options={PRODUCTS.filter((product) => !drafts.some((draft) => draft.key === product.key)).map((product) => ({
									label: product.label,
									value: product.key
								}))}
								defaultValue={undefined}
								onSelectedChange={(selected) => {
									const key = PRODUCTS.find((product) => product.key === selected)?.key;
									if (key) setDrafts((current) => [...current, empty_product(key)]);
								}}
								isDefaultValueNull={false}
							/>
						)}

						{drafts.length > 0 && (
							<div className="space-y-1">
								<span className="text-xs font-medium text-neutral-400">Vencimiento (opcional)</span>
								<Input
									type="datetime-local"
									placeholder="Sin vencimiento"
									value={expiresAt}
									onChange={setExpiresAt}
									clearable={true}
								/>
								<p className="text-xs text-neutral-400">Desde esa fecha el cliente vuelve a las condiciones de su nivel.</p>
							</div>
						)}

						{!drafts.length && (
							<Notice message="Sin productos, el cliente vuelve a las condiciones de su nivel al guardar." variant="info" />
						)}

						{formError && <Notice message={formError} variant="error" />}

						<div className="flex justify-end gap-3">
							<Button variant="secondary" className="w-fit" disabled={saving} onClick={() => setEditing(false)}>
								Cancelar
							</Button>
							<Button variant="primary" className="w-fit" type="submit" loading={saving}>
								Guardar
							</Button>
						</div>
					</form>
				</Dialog>
			)}

			{confirmRemove && (
				<ConfirmDialog
					title="Quitar condiciones personalizadas"
					message="El cliente volverá a las condiciones de su nivel en sus próximas operaciones. Las que ya tiene no cambian."
					confirmVariant="destructive"
					onConfirm={() => save({})}
					onClose={() => setConfirmRemove(false)}
				/>
			)}
		</div>
	);
}

function FieldHeader({ label, onRemove }: { label: string; onRemove: () => void }) {
	return (
		<div className="flex items-center justify-between">
			<span className="text-xs font-medium text-neutral-400">{label}</span>
			<button type="button" onClick={onRemove} className="text-xs text-neutral-400 hover:text-neutral-300">
				Quitar
			</button>
		</div>
	);
}
